import { css } from '@emotion/react';
import { Box, Text } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { MockInterviewStartButton } from '../MockInterviewStartButton';
import { LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './constants';

const META = [
  {
    captionKey: 'ai_interviews.landing.floating.fee_caption',
    captionDefault: '이용료',
    valueKey: 'ai_interviews.landing.floating.fee_value',
    valueDefault: '무료',
  },
  {
    captionKey: 'ai_interviews.landing.floating.limit_caption',
    captionDefault: '응시 횟수',
    valueKey: 'ai_interviews.landing.floating.limit_value',
    valueDefault: '1인 1회',
  },
  {
    captionKey: 'ai_interviews.landing.floating.job_caption',
    captionDefault: '지원 직무',
    valueKey: 'ai_interviews.landing.floating.job_value',
    valueDefault: '백엔드',
  },
] as const;

/**
 * 원티드 AI Championship floating CTA 와 동일 패턴:
 * - viewport 하단에 항상 fixed
 * - 데스크톱: 가운데 600px 글래스 카드 (bottom 24, radius 16)
 * - 좌측 메타 / 우측 다크 CTA
 * @see https://event.wanted.co.kr/ai-championship/2026
 */
export const FloatingBar = () => {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setMounted(true);
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setReady(true);
      return;
    }
    const id = window.requestAnimationFrame(() => {
      setReady(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  if (!mounted) {
    return null;
  }

  return createPortal(
    <Box
      css={[styles.bar, ready ? styles.barReady : styles.barEnter]}
      role="region"
      aria-label={String(t('ai_interviews.landing.floating.region', '모의면접 시작'))}
    >
      <Box css={styles.meta}>
        {META.map((item) => (
          <Box key={item.captionKey} css={styles.metaItem}>
            <Text css={styles.metaCaption}>{t(item.captionKey, item.captionDefault)}</Text>
            <Text css={styles.metaValue}>{t(item.valueKey, item.valueDefault)}</Text>
          </Box>
        ))}
      </Box>

      <MockInterviewStartButton />
    </Box>,
    document.body,
  );
};

const styles = {
  bar: css({
    position: 'fixed',
    left: '50%',
    bottom: 24,
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 33,
    width: 600,
    maxWidth: 'calc(100vw - 40px)',
    // height 고정 시 padding만 줄이면 안쪽 여백만 커져 어색해 보임 → 패딩으로 높이 결정
    minHeight: 64,
    height: 'auto',
    padding: `${LANDING_SPACE.chipPadY} ${LANDING_SPACE.chipPadY} ${LANDING_SPACE.chipPadY} ${LANDING_SPACE.pageX}`,
    borderRadius: 16,
    boxSizing: 'border-box',
    // frosted glass (Wanted .wds-wmn0j0::before)
    backgroundColor: 'rgba(234, 235, 236, 0.61)',
    backdropFilter: 'blur(40px)',
    WebkitBackdropFilter: 'blur(40px)',
    boxShadow: '0 12px 40px rgba(17, 19, 21, 0.12)',
    transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
    [LANDING_MOBILE_MQ]: {
      left: 0,
      right: 0,
      bottom: 0,
      width: 'auto',
      maxWidth: 'none',
      height: 'auto',
      minHeight: 72,
      padding: LANDING_SPACE.pageX,
      paddingBottom: `calc(${LANDING_SPACE.pageX} + env(safe-area-inset-bottom, 0px))`,
      gap: 0,
      borderRadius: 0,
      backgroundColor: '#fff',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      boxShadow: '0 -8px 24px rgba(17, 19, 21, 0.08)',
    },
  }),
  barEnter: css({
    opacity: 0,
    transform: 'translateX(-50%) translateY(120%)',
    [LANDING_MOBILE_MQ]: {
      transform: 'translateY(120%)',
    },
  }),
  barReady: css({
    opacity: 1,
    transform: 'translateX(-50%) translateY(0)',
    [LANDING_MOBILE_MQ]: {
      transform: 'none',
    },
  }),
  meta: css({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    minWidth: 0,
    flex: '0 0 auto',
    marginRight: 'auto',
    [LANDING_MOBILE_MQ]: {
      display: 'none',
    },
  }),
  metaItem: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    whiteSpace: 'nowrap',
  }),
  metaCaption: css({
    margin: 0,
    fontSize: LANDING_FONT.caption,
    lineHeight: 1.3,
    fontWeight: 600,
    color: 'rgba(23, 23, 25, 0.45)',
  }),
  metaValue: css({
    margin: 0,
    fontSize: LANDING_FONT.body,
    fontWeight: 700,
    lineHeight: 1.35,
    letterSpacing: '-0.02em',
    color: '#171719',
  }),
};
